/**
 * M-04 計算根拠。入力値・適用ルール・途中計算・最終結果を計算順に確認する。
 */

import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { endpoints } from '../../api/endpoints.js';
import { Card, EmptyState, ErrorMessage, Loading, Notice } from '../../components/ui.js';
import { formatDateLabel, formatDateTime, todayIso } from '../../lib/format.js';
import { useAsync } from '../../lib/useAsync.js';

type Tab = 'steps' | 'rules' | 'history';

export function CalculationPage(): React.JSX.Element {
  const navigate = useNavigate();
  const params = useParams<{ employeeId?: string; date?: string }>();
  const [tab, setTab] = useState<Tab>('steps');

  const employees = useAsync(() => endpoints.employees(), []);
  const [employeeId, setEmployeeId] = useState(params.employeeId ?? '');
  const [date, setDate] = useState(params.date ?? todayIso());

  const state = useAsync(
    () => (employeeId === '' ? Promise.resolve(null) : endpoints.calculation(employeeId, date)),
    [employeeId, date],
  );

  return (
    <>
      <Card title="対象の選択">
        <div className="grid gap-3 sm:grid-cols-[1fr_200px_auto]">
          <label className="block">
            <span className="field-label">スタッフ</span>
            <select
              className="input"
              value={employeeId}
              onChange={(event) => {
                setEmployeeId(event.target.value);
                navigate(`/admin/calculation/${event.target.value}/${date}`, { replace: true });
              }}
            >
              <option value="">選択してください</option>
              {(employees.data?.items ?? []).map((employee) => (
                <option key={employee.id} value={employee.id}>
                  {employee.employeeId}：{employee.name}
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="field-label">対象日</span>
            <input
              className="input"
              type="date"
              value={date}
              onChange={(event) => {
                setDate(event.target.value);
                if (employeeId !== '') {
                  navigate(`/admin/calculation/${employeeId}/${event.target.value}`, {
                    replace: true,
                  });
                }
              }}
            />
          </label>
        </div>
      </Card>

      <ErrorMessage error={state.error} />
      {state.loading && employeeId !== '' && <Loading />}
      {employeeId === '' && <EmptyState message="スタッフと対象日を選ぶと計算根拠を表示します。" />}

      {state.data && (
        <>
          <Card title={`${state.data.employee?.name ?? ''}｜${formatDateLabel(state.data.date)}`}>
            <div className="grid gap-3 text-sm sm:grid-cols-3 lg:grid-cols-5">
              <Field label="日種別" value={state.data.trace.dayType} />
              <Field label="入力パターン" value={state.data.trace.patternLabel} />
              <Field label="計算版" value={state.data.trace.engineVersion} />
              <Field label="入力識別値" value={state.data.trace.inputDigest.slice(0, 12)} />
              <Field
                label="保存済みとの一致"
                value={state.data.matchesStored ? '差分はありません' : '再計算が必要です'}
              />
            </div>
            {!state.data.matchesStored && (
              <Notice tone="warn">
                保存済みの計算結果と、いまの入力から求めた結果が一致しません。月次試算を実行してください。
              </Notice>
            )}
          </Card>

          <div className="flex gap-1">
            {(
              [
                ['steps', '計算ステップ'],
                ['rules', '適用ルール'],
                ['history', '計算履歴'],
              ] as [Tab, string][]
            ).map(([value, label]) => (
              <button
                key={value}
                type="button"
                className={`btn btn--sm ${tab === value ? 'btn--primary' : 'btn--secondary'}`}
                onClick={() => setTab(value)}
              >
                {label}
              </button>
            ))}
          </div>

          {tab === 'steps' && (
            <Card title="計算ステップ">
              <ol className="space-y-3">
                {state.data.trace.steps.map((step) => (
                  <li key={step.id} className="rounded-xl border border-line p-3">
                    <p className="text-[0.75rem] text-ink-muted">ステップ{step.order}</p>
                    <p className="font-semibold">{step.title}</p>
                    <p className="text-[0.78rem] text-ink-muted">{step.description}</p>
                    <p className="mt-1 font-mono text-[0.8rem] text-ink-sub">{step.expression}</p>
                    {step.inputs.length > 0 && (
                      <p className="mt-1 text-[0.78rem] text-ink-muted">
                        使用した入力：
                        {step.inputs.map((item) => `${item.label} ${item.value}`).join('、')}
                      </p>
                    )}
                    <p className="mt-1 text-sm">
                      {step.result.label}：
                      <span className="font-semibold">{step.result.value}</span>
                    </p>
                  </li>
                ))}
              </ol>
            </Card>
          )}

          {tab === 'rules' && (
            <Card title="適用ルール">
              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th>ルール</th>
                      <th>式</th>
                      <th>根拠</th>
                      <th>位置付け</th>
                    </tr>
                  </thead>
                  <tbody>
                    {state.data.rules.map((rule) => (
                      <tr key={rule.id}>
                        <td className="font-semibold whitespace-nowrap">{rule.name}</td>
                        <td className="font-mono text-[0.8rem]">{rule.expression}</td>
                        <td className="text-ink-muted">{rule.source}</td>
                        <td>
                          <span
                            className={`pill ${rule.status === 'confirmed' ? 'pill--ok' : 'pill--warn'}`}
                          >
                            {rule.status === 'confirmed' ? '確定' : '制度確認中'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}

          {tab === 'history' && (
            <Card title="計算履歴" description="以前の結果を上書きせず、計算ごとに記録しています。">
              {state.data.history.length === 0 ? (
                <EmptyState message="計算履歴はありません。" />
              ) : (
                <table className="table">
                  <thead>
                    <tr>
                      <th>計算日時</th>
                      <th>計算版</th>
                      <th>入力識別値</th>
                    </tr>
                  </thead>
                  <tbody>
                    {state.data.history.map((run, index) => (
                      <tr key={`${run.createdAt}-${index}`}>
                        <td>{formatDateTime(run.createdAt)}</td>
                        <td>{run.engineVersion}</td>
                        <td className="font-mono text-[0.8rem]">{run.inputDigest.slice(0, 16)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </Card>
          )}

          <div>
            <button
              type="button"
              className="btn btn--secondary btn--sm"
              onClick={() => downloadTrace(state.data!, `計算根拠_${state.data!.date}.json`)}
            >
              証跡を出力
            </button>
          </div>
        </>
      )}
    </>
  );
}

function Field({ label, value }: { label: string; value: string }): React.JSX.Element {
  return (
    <div>
      <p className="text-[0.75rem] text-ink-muted">{label}</p>
      <p className="font-semibold">{value}</p>
    </div>
  );
}

/** 監査用に計算根拠をそのまま書き出す */
function downloadTrace(data: unknown, filename: string): void {
  const blob = new Blob([JSON.stringify(data, null, 2)], {
    type: 'application/json;charset=utf-8',
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}
